/**
 * 이상한 방 - 와이어 프로토콜 (클라이언트 ↔ 호스트 메시지 · 코덱 · seq 문)
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 2절(스냅샷 두 벌) · 11.2절(클라이언트가 가진 것) · 11.3절(서버가 쥐는 것)
 *            로드맵 모델 2.4절(와이어) · 2.3절(방 데이터는 뷰가 아니라 별도 `rooms` 메시지)
 *
 * ## 세 갈래 - `rooms` · `step` · `clock`
 *
 *  - `rooms`  방 데이터(`WireRoom`). **페이즈 경계에서만** 간다(외우기 진입에 `original`, 수색 진입에 `mutated`,
 *             재접속·`resync`에 둘 다). 방은 수백 오브젝트라 매 스텝에 실으면 그것이 대역폭의 전부가 된다.
 *  - `step`   `toClientView` + **수신자 것만 걸러진** 효과. 상태가 바뀔 때마다 하나. 판정(`shotResolved`)이 여기
 *             실려 오므로 클라이언트는 이것을 기다린다 - 루프백에서도.
 *  - `clock`  `phaseElapsedMs` 하나. 2Hz. 틱마다 스냅샷을 보내지 않는 이유가 이 메시지다 - 시계 보간에 필요한
 *             것은 숫자 하나이고, 뷰 전체를 초당 60번 보내면 클라이언트가 렌더 대신 diff를 한다. 그래서 **시계만
 *             흐른 틱은 `step`을 내지 않는다**(`matchHost.commit`). 뷰의 시간 값(`phaseRemainingMs` · 원래 방 게이지)은
 *             마지막 `step.atMs`와 `clock`으로 클라이언트가 잇는다.
 *
 * ## `seq`는 단조 증가, 늦은 것은 버린다
 *
 * `rooms`·`step`은 매치 단위 카운터 하나에서 `seq`를 받는다(종류를 가르지 않는다 - 그래야 "rooms 뒤에 step"의
 * 순서도 같은 축에서 읽힌다). 클라이언트는 `seq <= last`인 것을 버린다(`createSeqGate`). 재접속·`resync`으로
 * 같은 스냅샷이 두 번 올 수 있고, 뒤늦게 도착한 옛 스텝이 새 뷰를 덮으면 잔탄이 잠깐 되살아난다.
 *
 * ## `PLAYER_EVENT_TYPES`는 가져다 쓴다 - 여기서 정의하지 않는다
 *
 * "클라이언트가 보낼 수 있는 것"의 원본은 `game/match/matchTypes.ts`다. 반대 방향이면 규칙 층이 전송 층을
 * import 하게 되어 화살표가 뒤집힌다. `decodeClient`는 그 상수로 `act.event.type`을 검사하고, 아니면
 * `not-player-event`다 - `tick`·`join`을 클라이언트가 보내는 것이 그 거절의 대상이다.
 *
 * ## 코덱은 JSON이고, 와이어에 `Set`·`Map`은 없다
 *
 * `JSON.stringify`는 `Map`·`Set`을 조용히 `{}`로 바꾼다. 뷰(`ClientView`)는 그래서 애초에 `Map`이 없게 만들어져
 * 있고(`matchView.ts` 머리말), 방은 `core/roomCodec`이 배열로 편다. 남는 것 하나가 `configure.patch.room.allowedVerbs`
 * (`ReadonlySet<Verb>`)다 - `RoomConfig`가 그 모양이라 이벤트도 그 모양이고, 와이어에서만 배열로 갈아 되돌린다.
 * 루프백이 **모든 메시지를 `encode → decode`로 통과**시키는 이유가 이것이다: 참조 공유를 끊는 것과 직렬화
 * 드리프트(새 필드에 `Set`이 끼는 것)를 잡는 것이 한 장치다. `structuredClone`은 `Map`을 살려 두어 드리프트를 못 잡는다.
 *
 * ## 형태 검증은 얕다 - 값이 아니라 모양만
 *
 * 판별자(`type`)와 숫자 필드(`seq`·`cseq`·`atMs`·`phaseElapsedMs`), 리듀서에 닿지 않는 메시지의 판별자 값(`lobby.op` ·
 * `presence.aim`의 모양), 그리고 **모든** 플레이어 이벤트 본문의 모양(`PLAYER_EVENT_SHAPES` - 이벤트 타입마다 리듀서가 읽는
 * 필드가 그 타입으로 있는가)까지만 본다. M4는 `applyChange.request`·`undoChange.id`만 봤고 나머지는 리듀서의 값 방어
 * (`raise.normalizeWager`가 `undefined`를 접는 것)에 기댔는데, 그것은 **값** 방어다 - 와이어 **모양** 방어는 여기 한 곳이어야
 * 리듀서 조각마다 "필드가 없으면"을 다시 적지 않는다(M6). 값의 뜻(셀이 방 안인가 · rot 범위 · 그 물건이 있는가 · 걸기 개수가
 * 예산 안인가)은 여전히 리듀서가 `core/verbs`·`normalizeWager`로 검증하고 거절한다 - 여기서도 하면 두 층이 같은 검사를 갖게 되고
 * 그중 하나만 갱신된다(`roomCodec` 머리말과 같은 결정). 모양을 보는 이유는 verbs가 값을 검증하려면 먼저 `request.obj.id`를
 * **읽어야** 하고, 없는 필드를 읽는 순간 그것은 거절이 아니라 호스트 핸들러의 TypeError다(11.3절 - 악의적 클라이언트 한 줄로
 * 호스트가 던지면 안 된다). 여기서 막는 것은 "리듀서에 닿으면 안 되는 것"(호스트 이벤트 위장 · 모양이 아닌 것)뿐이다.
 *
 * 모양 검사 함수들은 `protocolShapes.ts`에 있다(이 파일이 400줄을 넘어 갈랐다 - 코덱과 모양표는 이음매가 분명하다).
 *
 * `act.event`의 `by`는 **벗긴다.** 와이어에 `by`가 없는 것이 규약이고 호스트가 신원(토큰·루프백 좌석)으로 찍는다 - 클라이언트가
 * 적어 보낸 `by`를 남겨 두면 호스트의 `{ ...event, by }`가 덮어쓰기는 하지만, 그 덮어쓰기 한 줄에 위장 방어가 기대게 된다.
 * 거절하지 않고 벗기는 이유는 옛 클라이언트·디버그 훅이 실수로 실은 `by` 하나로 판이 멈추면 안 되기 때문이다(값은 어차피 무시된다).
 *
 * ## `hello`가 방을 정한다 - `lobby.create/join`은 자리만 있다
 *
 * `hello.roomCode`가 빈 문자열이면 **방을 만든다**(서버가 코드를 발급해 `welcome.roomCode`로 돌려준다 - 코드의 원본은 서버
 * `roomCodes.ts`라 클라이언트가 고를 수 없다). 코드가 있으면 그 방에 앉고, `token`이 있고 그 방의 좌석이면 재접속이다.
 * `lobby{op:'create'|'join'}`이 방 코드를 실을 자리가 없어 결국 `hello`가 방을 정하게 됐고, 두 op는 서버가 무시한다(와이어 호환으로
 * 남긴다). `addBot`·`removeBot`은 방장만 - 좌석 등록은 서버 층의 일이다(M2 기록 "hello·lobby는 방 등록의 일이라 호스트가 처리하지
 * 않는다"). `addBot.name`은 클라이언트가 자기 언어로 조립한 이름이다(`party.botName`) - 서버는 i18n을 모른다(로드맵 6절
 * "서버의 i18n 문장 합성" 기각). 사람 이름(`hello.name`)과 같은 규약이다.
 *
 * 그래서 이 파일의 `as unknown as ClientMessage` / `ServerMessage` / `PlayerEvent`가 **저장소에서 유일하게 허용되는
 * 캐스트 자리**다 - 코덱 경계에서 JSON을 타입으로 올리는 순간이고, 그 대가로 본문은 리듀서가 검증한다. 다른 파일에서
 * 같은 패턴이 보이면 그건 검사를 건너뛴 것이다.
 *
 * ## `browse`는 좌석이 없다 (M14)
 *
 * 방 목록을 보려는 소켓의 첫 메시지다(`hello`와 나란히). 서버는 `roomList` 하나로 답하고 **그 소켓은 어느 방에도
 * 붙지 않는다** - 이벤트를 보내면 닫는다(M14 규칙 3). 목록에 뜨는 조건 셋(공개 · 로비 · 빈 자리)은 전부 서버가
 * 판단하고 클라이언트는 받은 줄을 그리기만 한다(규칙 1, `ui/lobby.ts`의 "계산하지 않는다"의 연장).
 *
 * **기본은 비공개다.** `hello.visibility`가 없으면 그 방은 목록에 없다 - 옛 클라이언트가 만든 방이 갑자기
 * 남에게 보이지 않아야 하고, 그래서 이 마일스톤이 `PROTOCOL_VERSION`을 올리지 않는다(규칙 4).
 *
 * **목록에서 들어가는 것과 번호로 들어가는 것은 같은 `hello`다**(규칙 2). 두 길이 갈리면 재접속·유예·`superseded`가
 * 한쪽에서만 맞고, 그 어긋남은 "가끔 못 들어간다"로만 보인다.
 *
 * `net/`은 `core`·`game/match`·`game` 리프만 본다(`boundary.test.ts`). 서버(M6)가 이 파일을 그대로 import 한다.
 */

import type { WireRoom } from '../core/roomCodec'
import type { Cell3, PlayerId, Verb } from '../core/types'
import type { ClientView } from '../game/match/matchView'
import {
  PLAYER_EVENT_TYPES,
  type MatchConfig,
  type MatchEffectBody,
  type PlayerEvent,
  type PlayerEventType,
} from '../game/match/matchTypes'
import { isCellOrNull, isObject, isPlayerEventShape, type JsonObject } from './protocolShapes'

/* --- 상수 ------------------------------------------------------------------------ */

/** `hello.roomCode`가 이 값이면 "방을 만들어 달라"(머리말). 코드는 서버가 발급한다. */
export const CREATE_ROOM_CODE = ''

/**
 * 방의 좌석 상한(사람 + 이웃). 화면의 값이다 - 참가자 줄이 폰 세로 폭에서 두 줄이 되는 한계(`?bots=` 상한 7 + 나, M2 기록).
 * 서버(`room.ts`)가 `full`을 내는 기준이고 로비 UI가 [이웃 넣기]를 잠그는 기준이라 둘이 같은 상수를 봐야 한다 - 그래서 와이어 파일에 있다.
 */
export const MAX_ROOM_SEATS = 8

/**
 * 방 번호의 모양. 발급(엔트로피)은 서버에 남지만(`server/src/roomCodes.ts`) **모양은 여기가 정본**이다 -
 * 서버가 발급하고, `?room=`(`app/urlState.resolveRoomCode`)이 거르고, 로비 입력(`ui/lobby.tidyCodeInput`)이 다듬는
 * 같은 값이라 세 곳이 같은 것을 봐야 한다(`MAX_ROOM_SEATS`가 여기 있는 것과 같은 이유). 갈라 두었더니 실제로 갈렸다 -
 * URL 파서만 `[A-Z0-9]`를 허용해 `?room=IO01`이 클라이언트를 통과하고 서버에서 `no-room`이 됐다.
 *
 * I · O · 0 · 1을 뺀 32글자인 이유는 이 값이 **말로 전하고 손으로 치는** 값이라서다("ABCD로 들어와") -
 * 폰 글꼴에서 I/1 · O/0이 갈리지 않는다.
 */
export const ROOM_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'

export const ROOM_CODE_LENGTH = 4

const ROOM_CODE_SHAPE = new RegExp(`^[${ROOM_CODE_ALPHABET}]{${String(ROOM_CODE_LENGTH)}}$`)

/** 손으로 친 코드를 규격으로 - 공백을 벗기고 대문자로. 모양 검사(`isRoomCode`)는 이 뒤에 한다. */
export function normalizeRoomCode(raw: string): string {
  return raw.trim().toUpperCase()
}

export function isRoomCode(code: string): boolean {
  return ROOM_CODE_SHAPE.test(code)
}

/** 사람 이름의 최대 글자 수. 서버가 이 길이로 자르고, 로비 입력이 같은 값으로 막는다. */
export const MAX_PLAYER_NAME_LENGTH = 24

/**
 * 좌석에 박히기 전의 이름 다듬기 - 공백을 벗기고 길이를 자른다. **이름이 상태에 들어가는 모든 문은 이 함수를 지난다**:
 * `hello.name`(사람, `server/gateway.ts`)과 `lobby{addBot}.name`(이웃, `server/room.ts`), 그리고 그것을 미리 다듬는
 * 로비 입력(`ui/lobby.tidyNameInput`).
 *
 * 와이어 파일에 있는 이유는 `MAX_ROOM_SEATS`와 같다 - 서버와 UI가 같은 값을 봐야 한다. 다듬지 않는 문이 하나라도 남으면
 * 그 이름이 이후 **모든** `step`의 `ClientView.players[].name`으로 방 인원수만큼 곱해져 방송된다(소켓 상한 64KB × 좌석 8).
 * 빈 이름을 대신 지어 주지는 않는다 - 서버에는 사전이 없다(부르는 쪽이 정한다).
 */
export function tidyPlayerName(raw: string): string {
  return raw.trim().slice(0, MAX_PLAYER_NAME_LENGTH)
}

/* --- 버전 ------------------------------------------------------------------------ */

/** 와이어 버전. `hello.v`가 다르면 서버가 `error{version}`으로 끊는다. 메시지 모양이 호환을 깨는 날 올린다. */
export const PROTOCOL_VERSION = 1

/* --- 클라이언트 → 호스트 ---------------------------------------------------------- */

export type LobbyOp = 'create' | 'join' | 'addBot' | 'removeBot' | 'setVisibility'

/**
 * 방이 목록에 뜨는가(M14). **규칙이 아니다** - 리듀서는 이 값을 모르고 서버의 부기다(`server/src/room.ts` 머리말의
 * 좌석 등록과 같은 종류). 공개와 비공개의 차이는 목록에 뜨는가 하나뿐이고, 공개 방도 번호로 들어올 수 있다 -
 * 비공개를 "번호를 알아도 못 들어오는 방"으로 만들면 초대 링크·비밀번호가 따라오고 그건 다른 마일스톤이다.
 */
export type RoomVisibility = 'public' | 'private'

/**
 * 열린 방 한 줄(M14). 서버가 이미 셋을 판단했다 - 공개인가 · 로비인가 · 빈 자리가 있는가. 꽉 찬 방도 시작한
 * 방도 애초에 실리지 않으므로 화면은 "들어갈 수 있는가"를 다시 세지 않는다.
 *
 * 이름은 `tidyPlayerName`을 지난 것만 실린다(규칙 6) - 목록은 남에게 보이는 첫 문자열이다.
 * 동사·개수는 방장이 로비에서 정한 값이고(`LobbySettingsView`와 같은 둘), 그 둘이 "이 방이 어떤 판인가"의 전부다.
 */
export type RoomListing = Readonly<{
  code: string
  /** 방장 이름. */
  host: string
  players: number
  seatsMax: number
  /** **인당** 이상한 곳 수. 라운드 전체는 좌석 수 × 이 값이다(`LobbySettingsView.changeCount`와 같은 뜻). */
  changeCount: number
  allowedVerbs: readonly Verb[]
}>

/**
 * 클라이언트가 보내는 것. `act`에 `by`가 없다 - 호스트가 신원(토큰·루프백 좌석)으로 찍는다. 자기 이름을 적어
 * 보내는 것이 곧 위장이다. `presence`·`reaction`·`hurry`·`envelope`는 M8이라 `presence`만 자리를 잡아 두었다
 * (10Hz, 규칙 아님).
 */
export type ClientMessage =
  /**
   * 첫 메시지. `roomCode`가 `CREATE_ROOM_CODE`면 방을 만든다. `token`은 재접속(머리말).
   * `visibility`는 **만들 때만** 뜻이 있다 - 없으면 `private`이라 지금 동작이 곧 기본값이다(M14 규칙 4).
   */
  | Readonly<{ type: 'hello'; v: number; roomCode: string; name: string; token?: string; visibility?: RoomVisibility }>
  /** 좌석 없이 방 목록만 받는 첫 메시지(M14 - 머리말). 같은 소켓의 다음 `browse`가 새로고침이다. */
  | Readonly<{ type: 'browse'; v: number }>
  /** `name`은 `addBot`에서만, `visibility`는 `setVisibility`에서만 뜻이 있다(둘 다 방장만). */
  | Readonly<{ type: 'lobby'; op: LobbyOp; name?: string; visibility?: RoomVisibility }>
  /** `cseq`는 클라이언트 쪽 일련번호. 거절(`rejected`)이 어느 요청의 것인지 짝지어 준다. */
  | Readonly<{ type: 'act'; cseq: number; event: PlayerEvent }>
  | Readonly<{ type: 'presence'; aim: Cell3 | null }>
  /** 방 둘 + 뷰를 다시 달라. 재접속·탭 복귀. */
  | Readonly<{ type: 'resync' }>
  | Readonly<{ type: 'ping'; t: number }>

/* --- 호스트 → 클라이언트 ---------------------------------------------------------- */

/** `rooms` 메시지의 몸. `null`은 "지금 그 방을 보여 줄 수 없다"(외우기 중의 변경본 · 로비). */
export type RoomsPayload = Readonly<{ original: WireRoom | null; mutated: WireRoom | null }>

export type RejectReason = 'not-player-event' | 'phase' | 'role' | 'unknown'

/**
 * `in-progress`는 M6이 더했다 - 진행 중인 방에 새 사람이 앉을 수 없는 것(리듀서 `onJoin`은 로비에서만 앉힌다)을 `full`로
 * 말하면 "가득 찼다"가 거짓이다. `full`은 좌석 수 상한(서버 `room.ts`)의 것이다.
 * 배열이 원본인 이유는 화면이 코드마다 문장(`net.error.<code>`)을 가져야 하고 그 사전 검사(`i18n/index.test.ts`)가 목록을 돌기 때문이다.
 */
export const ERROR_CODES = ['version', 'no-room', 'full', 'in-progress', 'superseded'] as const

export type ErrorCode = (typeof ERROR_CODES)[number]

export type ServerMessage =
  /** 좌석이 났다. 뷰와 방을 함께 싣는다 - 첫 화면이 두 메시지를 기다릴 이유가 없다. */
  | Readonly<{
      type: 'welcome'
      playerId: PlayerId
      token: string
      roomCode: string
      view: ClientView
      rooms: RoomsPayload
      /** 이 방이 목록에 뜨는가(M14). 옛 서버는 싣지 않으므로 선택 필드이고, 없으면 `private`으로 읽는다. */
      visibility?: RoomVisibility
    }>
  | Readonly<{ type: 'rooms'; seq: number; original: WireRoom | null; mutated: WireRoom | null }>
  /**
   * 효과는 **수신자 것만** 걸러져 있다(`to === 'all' | me`). `atMs`는 호스트 시계, `phaseElapsedMs`는 같은 순간의 페이즈
   * 경과 - 클라이언트 시계(`game/matchClock`)가 이 둘로 기준을 잡고 그 뒤의 `clock`과 대조한다. 경과가 없으면 `step` 직후
   * 첫 `clock`은 기준만 잡을 수 있어 그 사이의 드리프트(자막 잠금 · 지연)를 영영 못 잡는다.
   */
  | Readonly<{
      type: 'step'
      seq: number
      atMs: number
      phaseElapsedMs: number
      view: ClientView
      effects: readonly MatchEffectBody[]
    }>
  | Readonly<{ type: 'clock'; phaseElapsedMs: number }>
  | Readonly<{ type: 'presence'; by: PlayerId; aim: Cell3 | null }>
  /** 리듀서가 참조 동일로 무시한 요청. 뷰의 불리언이 이미 막았을 것이므로 여기까지 온 것은 지연이나 위장이다. */
  | Readonly<{ type: 'rejected'; cseq: number; reason: RejectReason }>
  | Readonly<{ type: 'pong'; t: number; serverMs: number }>
  /** `browse` 소켓의 유일한 답(M14). 좌석이 없으므로 `seq`도 없다 - 목록은 상태가 아니라 그 순간의 스냅샷이다. */
  | Readonly<{ type: 'roomList'; rooms: readonly RoomListing[] }>
  /**
   * 이 방의 공개 여부가 바뀌었다(M14 규칙 5 - 방장이 로비에서). 좌석 전원에게 간다: 방장은 자기 토글이 먹은 것을
   * 이 메시지로 확인하고(낙관적으로 칠하지 않는다 - 거절을 서버가 정한다), 나머지는 얼굴이 흔들리지 않는다.
   * 첫 값은 `welcome.visibility`다.
   */
  | Readonly<{ type: 'visibility'; value: RoomVisibility }>
  | Readonly<{ type: 'error'; code: ErrorCode }>

export type WireMessage = ClientMessage | ServerMessage

/* --- 플레이어 이벤트 검사 ------------------------------------------------------------ */

const PLAYER_EVENT_TYPE_SET: ReadonlySet<string> = new Set(PLAYER_EVENT_TYPES)

/** 클라이언트가 보낼 수 있는 이벤트 타입인가. 원본은 `matchTypes.PLAYER_EVENT_TYPES`다(머리말). */
export function isPlayerEventType(value: unknown): value is PlayerEventType {
  return typeof value === 'string' && PLAYER_EVENT_TYPE_SET.has(value)
}

/* --- 코덱 ------------------------------------------------------------------------ */

export type DecodeFailure = 'malformed' | 'unknown-type' | 'not-player-event'

export type DecodeResult<T> = Readonly<{ ok: true; message: T }> | Readonly<{ ok: false; reason: DecodeFailure }>

const CLIENT_TYPES: ReadonlySet<string> = new Set<ClientMessage['type']>([
  'hello', 'browse', 'lobby', 'act', 'presence', 'resync', 'ping',
])

const SERVER_TYPES: ReadonlySet<string> = new Set<ServerMessage['type']>([
  'welcome', 'rooms', 'step', 'clock', 'presence', 'rejected', 'pong', 'roomList', 'visibility', 'error',
])

/** 이 종류의 서버 메시지는 `seq`를 싣는다. 없거나 숫자가 아니면 `malformed`다. */
const SEQ_TYPES: ReadonlySet<string> = new Set<ServerMessage['type']>(['rooms', 'step'])

const LOBBY_OPS: ReadonlySet<unknown> = new Set<LobbyOp>(['create', 'join', 'addBot', 'removeBot', 'setVisibility'])

const VISIBILITIES: ReadonlySet<unknown> = new Set<RoomVisibility>(['public', 'private'])

/** 있으면 공개 여부여야 한다. 없는 것은 정상이다 - 기본은 비공개다(머리말). */
function isVisibilityOrAbsent(value: unknown): boolean {
  return value === undefined || VISIBILITIES.has(value)
}

/**
 * 와이어 위의 `configure` 패치. `allowedVerbs`만 배열이다(머리말 "와이어에 `Set`은 없다").
 * `room`을 통째로 다시 적는 이유는 `Partial<RoomConfig>`의 나머지 두 필드가 그대로 따라오게 하기 위해서다.
 */
type WireRoomPatch = Readonly<Omit<Partial<MatchConfig['room']>, 'allowedVerbs'> & { allowedVerbs?: readonly Verb[] }>

function roomPatchToWire(room: Partial<MatchConfig['room']>): WireRoomPatch {
  const { allowedVerbs, ...rest } = room
  return allowedVerbs === undefined ? rest : { ...rest, allowedVerbs: [...allowedVerbs] }
}

function roomPatchFromWire(room: JsonObject): Partial<MatchConfig['room']> {
  const { allowedVerbs, ...rest } = room
  const patch = rest as Partial<MatchConfig['room']>
  return Array.isArray(allowedVerbs) ? { ...patch, allowedVerbs: new Set(allowedVerbs as Verb[]) } : patch
}

/** 이벤트 하나를 와이어 모양으로. `configure`만 손댄다 - 나머지 이벤트는 이미 JSON 그대로다. */
function eventToWire(event: PlayerEvent): unknown {
  if (event.type !== 'configure' || event.patch.room === undefined) return event
  return { ...event, patch: { ...event.patch, room: roomPatchToWire(event.patch.room) } }
}

function eventFromWire(event: JsonObject): PlayerEvent {
  if (event['type'] !== 'configure') return event as unknown as PlayerEvent
  const patch = event['patch']
  if (!isObject(patch) || !isObject(patch['room'])) return event as unknown as PlayerEvent
  return { ...event, patch: { ...patch, room: roomPatchFromWire(patch['room']) } } as unknown as PlayerEvent
}

/**
 * 메시지 → 문자열. 클라이언트·서버 양방향이 같은 함수다 - 종류를 가르는 것은 받는 쪽(`decodeClient`/`decodeServer`)이고,
 * 보내는 쪽이 방향을 알 필요는 없다.
 */
export function encode(msg: WireMessage): string {
  if (msg.type === 'act') return JSON.stringify({ ...msg, event: eventToWire(msg.event) })
  return JSON.stringify(msg)
}

function parse(text: string): JsonObject | null {
  try {
    const value: unknown = JSON.parse(text)
    return isObject(value) ? value : null
  } catch {
    return null
  }
}

/**
 * 클라이언트 메시지를 읽는다. `act`의 `event.type`이 플레이어 이벤트가 아니면 `not-player-event` -
 * 그것이 "호스트 이벤트를 클라이언트가 보내면 거절한다"(`matchTypes.HostEvent` 주석)의 실제 자리다.
 */
export function decodeClient(text: string): DecodeResult<ClientMessage> {
  const raw = parse(text)
  if (raw === null || typeof raw['type'] !== 'string') return { ok: false, reason: 'malformed' }
  if (!CLIENT_TYPES.has(raw['type'])) return { ok: false, reason: 'unknown-type' }

  if (raw['type'] === 'act') {
    const event = raw['event']
    if (typeof raw['cseq'] !== 'number' || !isObject(event)) return { ok: false, reason: 'malformed' }
    const eventType = event['type']
    if (!isPlayerEventType(eventType)) return { ok: false, reason: 'not-player-event' }
    if (!isPlayerEventShape(eventType, event)) return { ok: false, reason: 'malformed' }
    // `by`는 벗긴다(머리말) - 남는 것은 호스트가 찍는다.
    const body: JsonObject = { ...event }
    delete body['by']
    return { ok: true, message: { type: 'act', cseq: raw['cseq'], event: eventFromWire(body) } }
  }
  if (
    raw['type'] === 'hello' &&
    (typeof raw['v'] !== 'number' ||
      typeof raw['roomCode'] !== 'string' ||
      typeof raw['name'] !== 'string' ||
      (raw['token'] !== undefined && typeof raw['token'] !== 'string') ||
      !isVisibilityOrAbsent(raw['visibility']))
  ) {
    return { ok: false, reason: 'malformed' }
  }
  // `browse`는 버전 하나가 전부다 - 좌석도 이름도 없다(머리말).
  if (raw['type'] === 'browse' && typeof raw['v'] !== 'number') return { ok: false, reason: 'malformed' }
  if (raw['type'] === 'ping' && typeof raw['t'] !== 'number') return { ok: false, reason: 'malformed' }
  if (
    raw['type'] === 'lobby' &&
    (!LOBBY_OPS.has(raw['op']) ||
      (raw['name'] !== undefined && typeof raw['name'] !== 'string') ||
      !isVisibilityOrAbsent(raw['visibility']))
  ) {
    return { ok: false, reason: 'malformed' }
  }
  if (raw['type'] === 'presence' && !isCellOrNull(raw['aim'])) return { ok: false, reason: 'malformed' }
  return { ok: true, message: raw as unknown as ClientMessage }
}

/**
 * 디코드에 실패한 텍스트가 `act`였다면 그 `cseq`. 서버가 `malformed`·`not-player-event`를 `rejected{cseq}`로 되돌려 주려면
 * 실패한 메시지의 번호가 필요한데, `DecodeResult`의 실패 쪽에 `cseq`를 싣지 않는 이유는 실패 모양을 두 겹으로 만들지 않기
 * 위해서다(실패 경로에서만 한 번 더 파싱한다 - 드물다). 번호가 없으면 `null` - 되돌려 줄 곳이 없다.
 */
export function cseqOf(text: string): number | null {
  const raw = parse(text)
  if (raw === null || raw['type'] !== 'act' || typeof raw['cseq'] !== 'number') return null
  return raw['cseq']
}

/** 서버 메시지를 읽는다. `seq`를 싣는 종류는 숫자인지까지 본다 - 문(`createSeqGate`)이 그 값으로 순서를 가린다. */
export function decodeServer(text: string): DecodeResult<ServerMessage> {
  const raw = parse(text)
  if (raw === null || typeof raw['type'] !== 'string') return { ok: false, reason: 'malformed' }
  if (!SERVER_TYPES.has(raw['type'])) return { ok: false, reason: 'unknown-type' }
  if (SEQ_TYPES.has(raw['type']) && typeof raw['seq'] !== 'number') return { ok: false, reason: 'malformed' }
  if (
    raw['type'] === 'step' &&
    (typeof raw['atMs'] !== 'number' ||
      typeof raw['phaseElapsedMs'] !== 'number' ||
      !isObject(raw['view']) ||
      !Array.isArray(raw['effects']))
  ) {
    return { ok: false, reason: 'malformed' }
  }
  /*
   * `welcome`도 몸을 본다 - 클라이언트가 **가장 깊게 파고드는** 메시지가 그것이다(`wsTransport`가 `token`·`roomCode`·`playerId`를,
   * `app/lobbyFlow`가 `rooms.*.templateId`를 무조건 읽는다). 필드 하나가 빠진 `welcome`이 통과하면 `ws.onmessage` 안에서
   * TypeError가 나고 - 그 예외를 잡는 자리가 없다 - 로비가 영원히 "들어가는 중"에 굳는다. 검사는 `step`과 같은 깊이(모양 한 겹)다.
   */
  if (
    raw['type'] === 'welcome' &&
    (typeof raw['playerId'] !== 'string' ||
      typeof raw['token'] !== 'string' ||
      typeof raw['roomCode'] !== 'string' ||
      !isObject(raw['view']) ||
      !isObject(raw['rooms']))
  ) {
    return { ok: false, reason: 'malformed' }
  }
  /*
   * `roomList`·`visibility`도 몸을 본다(M14). `welcome`과 같은 이유다 - 화면이 무조건 읽는 필드라, 모양이 아닌 것이
   * 통과하면 `ws.onmessage` 안에서 TypeError가 나고 그 예외를 잡는 자리가 없다. 줄 **안**의 필드는 보지 않는다:
   * 목록은 그리기만 하는 값이라 없는 칸은 빈 칸으로 나올 뿐 판을 어긋나게 하지 않는다(`step`의 뷰와 같은 깊이).
   */
  if (raw['type'] === 'roomList' && !Array.isArray(raw['rooms'])) return { ok: false, reason: 'malformed' }
  if (raw['type'] === 'visibility' && !VISIBILITIES.has(raw['value'])) return { ok: false, reason: 'malformed' }
  return { ok: true, message: raw as unknown as ServerMessage }
}

/* --- seq 문 ---------------------------------------------------------------------- */

export type SeqGate = Readonly<{
  /** 이 `seq`를 받아들일 것인가. 받아들이면 그 값이 새 기준이다. `seq <= last`는 거짓 - 늦은 것은 버린다. */
  accept: (seq: number) => boolean
  last: () => number
}>

/** 첫 메시지 전의 기준. 어떤 실제 `seq`(호스트는 1부터)보다 작다. */
const BEFORE_FIRST_SEQ = 0

/**
 * 클라이언트의 `seq` 문. `rooms`·`step` 둘 다 이 하나를 지난다(머리말 - 같은 축).
 * 유한수가 아닌 `seq`는 거짓이다 - `NaN <= last`는 항상 거짓이라 문을 통과하고, 그 뒤 기준이 `NaN`이 되면 모든 것을 막는다.
 */
export function createSeqGate(): SeqGate {
  let last = BEFORE_FIRST_SEQ
  return {
    accept(seq: number): boolean {
      if (!Number.isFinite(seq) || seq <= last) return false
      last = seq
      return true
    },
    last: () => last,
  }
}
